import React, { useState, useEffect, useRef, useCallback } from "react"
import { CONSTANTS } from "@data"

import { css, q, qAll, logTool, listenDocEvent, focusTools, keyboard } from "@jsUtils"

import { Social } from "@shared"

import Styles from "./nav.module.scss"

import NavMain from "./NavMain"
import NavSecondary from "./NavSecondary"

const { keyId } = keyboard
const { scopeTab } = focusTools

const { MISC, EVENTS } = CONSTANTS
const { KEYEVENT } = EVENTS

const log = logTool()

const labels = {
  visualClosedText: "More",
  visualOpenText: "Close",
  readerClosedText: "Show more navigation items",
  readerOpenText: "Close main menu",
  mainMenu: "Main Menu"
}

const objs = {
  menuSetContent: "menu-content"
}

let bodyTag = null
let grid = null
let navPage = null

/**
 * Nav is the main top of page navigation for the site
 * @param {object} props - React props
 * @param {string} props.theme - site theme (dark/light etc.)
 * @param {string} props.pageColor - foreground on closed nav color
 */
const Nav = props => {
  const { theme, pageColor } = props
  const { visualClosedText, visualOpenText, readerClosedText, readerOpenText, mainMenu } = labels

  log("Nav:", { Styles, props, labels, pageColor })

  const [menu, updateMenu] = useState({
    currentReaderText: readerClosedText,
    hideMain: true,
    hidePrimary: false,
    hasOpened: false
  })

  const isOpen = useRef(false)
  const buttonRef = useRef(null)
  const mainRef = useRef(null)

  const updateOpen = data => {
    isOpen.current = data
  }

  const logoItem = () => qAll(`.${MISC.LOGO_CLASS}`)[0]

  const registerClick = () => {
    const open = isOpen.current

    // add a class to the body
    if (!bodyTag) {
      bodyTag = q("body")
    }
    // we compose navPageOpen from other sources
    const navPageOpen = Styles.navPageOpen.split(" ")
    navPageOpen.forEach(i => bodyTag.classList.toggle(i))

    // update text and aria-labels labels
    const nextText = open ? readerClosedText : readerOpenText
    const nextState = !open
    const hidePrimary = !open
    const hideMain = open

    // remove default
    bodyTag.classList.remove(Styles.navPage)
    if (!nextState) {
      bodyTag.classList.add(Styles.navPageClosed)
      bodyTag.classList.remove("exo-nav-open")
      bodyTag.classList.add("exo-nav-closed")
      buttonRef.current.focus()
    } else {
      bodyTag.classList.remove(Styles.navPageClosed)
      bodyTag.classList.remove("exo-nav-closed")
      bodyTag.classList.add("exo-nav-open")
    }

    updateOpen(nextState)

    updateMenu(menuState => ({
      ...menuState,
      currentReaderText: nextText,
      hasOpened: true,
      hidePrimary,
      hideMain
    }))
  }

  const onkeyboard = e => {
    const open = isOpen.current
    let keyPress = false
    keyPress = keyId(e)
    if (open && keyPress.escape) {
      registerClick()
    }
    if (open && keyPress.tab) {
      scopeTab({
        node: mainRef.current,
        first: logoItem(),
        event: e
      })
    }
  }

  const menuClick = e => {
    e.preventDefault()
    registerClick()
  }

  const handleRouteChange = useCallback(() => {
    updateOpen(false)
  }, [updateOpen])

  // set up, tear down
  useEffect(() => {
    grid = q("header > div")
    grid.classList.add(Styles.headerGrid)
    if (navPage === null) {
      navPage = true
      q("body").classList.add(Styles.navPage)
      q("body").classList.add("exo-nav-closed")
    }
    listenDocEvent(KEYEVENT, onkeyboard)
    window.addEventListener("prerouteupdate", handleRouteChange)
    return () => {
      q(`.${Styles.tacoBurger}`).classList.remove(Styles.tacoClosed)
      navPage = null
      listenDocEvent(KEYEVENT, onkeyboard, false)
      window.removeEventListener("prerouteupdate", handleRouteChange)
    }
  }, [])

  return (
    <div
      className={css(
        Styles.navWrapper,
        Styles[`theme__${theme}`],
        Styles[`pageColor__${pageColor}`]
      )}
    >
      <nav role="navigation" aria-labelledby={objs.menuSetContent} id="nav-menu">
        <div className={Styles.controls}>
          <button
            ref={buttonRef}
            type="button"
            aria-expanded={isOpen.current}
            aria-label={menu.currentReaderText}
            aria-controls={objs.menuSetContent}
            onClick={menuClick}
          >
            <div className={Styles.tacoBurger}>
              <span className={css(Styles.tacoItem, Styles.burgerTop)} />
              <span className={css(Styles.tacoItem, Styles.burgerBottom)} />
            </div>
            <div
              className={Styles.menuInstruction}
              role="presentation"
              aria-hidden="true"
              data-js-focus-visible
            >
              <div className={Styles.more}>{visualClosedText}</div>
              <div className={Styles.close}>{visualOpenText}</div>
            </div>
          </button>
        </div>
        <div id={objs.menuSetContent} className={Styles.menuSet} aria-label={mainMenu}>
          <NavMain
            analytics="HEADER"
            className={Styles.primaryNav}
            hidden={menu.hidePrimary}
            linkStyleMap={{
              exo: Styles.exo,
              vis: Styles.vis,
              tech: Styles.tech
            }}
          />
          <div className={Styles.fullMenu} ref={mainRef}>
            <NavMain
              analytics="MENU"
              className={Styles.mainNav}
              hidden={menu.hideMain}
              linkStyleMap={{
                exo: Styles.exo,
                vis: Styles.vis,
                tech: Styles.tech
              }}
            />
            <NavSecondary analytics="MENU" className={Styles.secondaryNav} hidden={menu.hideMain}>
              <Social hidden={menu.hideMain} parentClass={Styles.social} />
            </NavSecondary>
          </div>
        </div>
      </nav>
    </div>
  )
}

export default Nav
